<script setup lang="ts">
import { onMounted, watch, ref } from 'vue';
import { transformCodeToDate, formatDateToFrench, getZoneLabel } from '../functions/utils';
import { useDataStore } from '../stores/useDataStore';
import { useAuthStore } from '../stores/useAuthStore'
import { useReactorStateStore } from '../stores/useReactorStateStore';
import {
  getDeltaAbsorbanceGrowth,
  getThresholdsClass, latestManualUpdateFormatted,
  computeAge, prochainesRecoltesParEspeceEtJour, days,
  onCommentInput, saveCommentaire, localCommentaire, onInput, initializeLocalCommentaire
} from '@/functions/functions_pilotage';
import { culturesGroupedByZone } from '@/functions/functions_pilotage';
import { fetchCommentaire, fetchManualValues } from '../functions/functions_pilotage_api';
import Maintenance from '@/components/Maintenance.vue';
import { isMainSpecie } from '@/functions/functions_cultures';

const authStore = useAuthStore();
const dataStore = useDataStore();
const reactorStateStore = useReactorStateStore();
const isInitialLoad = ref(true);

const localThresholds = ref<Record<string, (number | null)[]>>({});

onMounted(async () => {
  dataStore.isPilotageLoading = true;
  await dataStore.loadBasicData();
  await reactorStateStore.loadData();
  await fetchManualValues();
  await fetchCommentaire();
  await initializeLocalCommentaire();
  dataStore.isPilotageLoading = false;
  isInitialLoad.value = false;
});

watch(
  () => dataStore.thresholds,
  (newThresholds) => {
    localThresholds.value = JSON.parse(JSON.stringify(newThresholds));
    isInitialLoad.value = false;
  },
  { immediate: true, deep: true }
);

watch(() => dataStore.commentaire, (newVal) => {
  localCommentaire.value = newVal?.commentaire ?? '';
});

// Function to get productivity for a culture
const getProductivity = (culture: any) => {
  if (!culture || !culture.pbr || !culture.specie) {
    console.warn(`Invalid culture object: ${JSON.stringify(culture)}`);
    return 0;
  }

  const isMain = isMainSpecie(culture.specie.id);
  const latestHarvest = reactorStateStore.filteredHarvestYield
    .filter(h => h.pbr === culture.pbr.numero_pbr)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

  if (!latestHarvest) {
    return 0;
  }

  const productivity = isMain && latestHarvest.productiviteBsMain != null
    ? latestHarvest.productiviteBsMain
    : latestHarvest.productiviteBs ?? 0;

  if (!productivity || isNaN(productivity)) {
    return 0;
  }

  return productivity;
};
</script>

<template>
  <Maintenance />
  <div v-if="authStore.isAuthenticated">
    <div v-if="authStore.scopes.includes('editor')">

      <h1>{{ $t('controlTitle') }}</h1>

      <div>

        <!-- Loader pour le chargement général des données -->
        <div v-if="dataStore.isPilotageLoading" class="spinner-container module">
          <div class="spinner"></div>
          <p>{{ $t('loadingData') }}</p>
        </div>


        <div v-else class="module module-pilotage">

          <div class="table-container-pilotage">
            <table class="styled-table sticky-head">
              <colgroup>
                <col style="width: 4%;" /> <!-- Zone -->
                <col style="width: 5%;" /> <!-- PBR -->
                <col style="width: 5%;" /> <!-- Souche -->
                <col style="width: 6%;" /> <!-- Date de mise en culture -->
                <col style="width: 19%;" /> <!-- Physiologie -->
                <col style="width: 19%;" /> <!-- Actions décidées -->
                <col style="width: 6%;" /> <!-- Indicateur croissance -->
                <col style="width: 6%;" /> <!-- Productivités (kg/m3/an) -->
                <col style="width: 5%;" /> <!-- Fx (%) et Lutéine (%) -->
                <col style="width: 6%;" /> <!-- Date des données Fx -->
                <col style="width: 5%;" /> <!-- Âge (jours) -->
                <col style="width: 10%;" /> <!-- Prochaine récoltes -->
              </colgroup>

              <thead>
                <tr>
                  <th></th>
                  <th :title="'PBR'">PBR</th>
                  <th :title="$t('specie')">{{ $t('specie') }}</th>
                  <th :title="$t('start')">{{ $t('start') }}</th>
                  <th :title="$t('physiology')">{{ $t('physiology') }}</th>
                  <th :title="$t('decidedActions')">{{ $t('decidedActions') }}</th>
                  <th :title="$t('ic_tooltip')">{{ $t('ic') }}</th> <!-- Indice de croissance -->
                  <th :title="$t('productivity')">{{ $t('productivity') }}</th>
                  <th :title="$t('mi_tooltip')">{{ $t('mi') }}</th> <!-- Molécule d'intéret -->
                  <th :title="$t('analysis')">{{ $t('analysis') }}</th>
                  <th :title="$t('daysold')">{{ $t('daysold') }}</th>
                  <th :title="$t('harvest')">{{ $t('harvest') }}</th>
                </tr>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th>(%)</th>
                  <th>{{ $t('kg/m3/y') }}</th>
                  <th>(%)</th>
                  <th></th>
                  <th>({{ $t('d') }})</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(cultures, zone) in culturesGroupedByZone" :key="zone">
                  <tr v-for="(culture, index) in cultures" :class="zone" :key="culture.id">
                    <!-- Affiche la cellule zone avec rowspan une seule fois par groupe -->
                    <td v-if="index === 0" :rowspan="cultures.length" class="vertical-zone">
                      {{ getZoneLabel(zone as 'zoneA' | 'zoneB' | 'zoneC') }}
                    </td>

                    <td>{{ culture.pbr.numero_pbr }}</td>
                    <td>{{ culture.specie.specie_code }}</td>
                    <td>{{ formatDateToFrench(transformCodeToDate(culture.startdaycode.startday_code)) }}</td>

                    <!-- CHAMPS ÉDITABLES -->
                    <td>
                      <input class="editable-cell" type="text"
                        :value="dataStore.manualValues[culture.id]?.physiologie ?? ''"
                        @blur="e => onInput(e, culture.id, 'physiologie')" />
                    </td>
                    <td>
                      <input class="editable-cell" type="text"
                        :value="dataStore.manualValues[culture.id]?.actions_prevue ?? ''"
                        @blur="e => onInput(e, culture.id, 'actions_prevue')" />
                    </td>

                    <!-- INFOS CALCULÉES -->
                    <td :class="getThresholdsClass('ic', culture.specie.id, getDeltaAbsorbanceGrowth(culture))">
                      {{ getDeltaAbsorbanceGrowth(culture) != 0 ? getDeltaAbsorbanceGrowth(culture) : '' }}
                      <!-- INDICE DE CROISSANCE -->
                    </td>
                    <td :class="getThresholdsClass('ProdMs', culture.specie.id, getProductivity(culture))">{{
                      getProductivity(culture) != 0 ? getProductivity(culture).toFixed(2) : '' }}</td>
                    <td>
                      {{
                        (() => {
                          const recent = dataStore.getMostRecentAnalyseWithFx(culture.analyses);
                          return recent ? dataStore.getConcentration(recent) : '';
                        })()
                      }}
                    </td>
                    <td>
                      {{ dataStore.getMostRecentAnalyseWithFx(culture.analyses)?.daycode
                        ?
                        formatDateToFrench(transformCodeToDate(dataStore.getMostRecentAnalyseWithFx(culture.analyses)!.daycode))
                        : '' }}
                    </td>
                    <td>{{ computeAge(culture) }}</td>
                    <td>
                      <select class="styled-dropdown editable-cell"
                        :value="dataStore.manualValues[culture.id]?.prochaine_recolte ?? ''"
                        @change="e => onInput(e, culture.id, 'prochaine_recolte')">
                        <option value="">{{ $t('Day') }}</option>
                        <option v-for="day in days" :value="day" :key="day">
                          {{ $t(`${day}`) }}
                        </option>
                      </select>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <p v-if="latestManualUpdateFormatted" style="font-weight: bold; font-size: 0.7em;">
            {{ $t('lastUpdate') }} {{ latestManualUpdateFormatted }}
          </p>
        </div>

        <div v-if="!dataStore.isPilotageLoading" class="module-recoltes bloc_texte">
          <h2>{{ $t('topicsTitle') }}</h2>
          <textarea :value="localCommentaire" rows="10" @input="onCommentInput" @blur="saveCommentaire" />
        </div>

        <div v-if="!dataStore.isPilotageLoading" class="module-recoltes">
          <h2>{{ $t('nextHarvests') }}</h2>

          <table class="styled-table">
            <thead>
              <tr>
                <th>{{ $t('species') }}</th>
                <th v-for="day in days" :key="day">{{ $t(`${day}`) }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(jours, espece) in prochainesRecoltesParEspeceEtJour" :key="espece">
                <td>{{ espece }}</td>
                <td v-for="day in days" :key="day">
                  {{ jours[day]?.join(', ') || '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!dataStore.isPilotageLoading" class="module">
          <h2>{{ $t('legendTitle') }}</h2>
          
          <div class="color-legend">
            <h4>{{ $t('growthIndicatorsTitle') }}</h4>
            <ul>
              <li><span class="color-box growth-red"></span> {{ $t('legend.item1') }}</li>
              <li><span class="color-box growth-orange"></span> {{ $t('legend.item2') }}</li>
              <li><span class="color-box growth-light-green"></span> {{ $t('legend.item3') }}</li>
              <li><span class="color-box growth-dark-green"></span> {{ $t('legend.item4') }}</li>
            </ul>
          </div>

          <div class="color-legend">
            <h4>{{ $t('productivityDbTitle') }}</h4>
            <ul>
              <li><span class="color-box prod-pink"></span> {{ $t('legend.item5') }}</li>
              <li><span class="color-box prod-orange"></span> {{ $t('legend.item6') }}</li>
              <li><span class="color-box prod-green"></span> {{ $t('legend.item7') }}</li>
            </ul>
          </div>
        </div>

      </div>
    </div>

    <div v-if="!authStore.scopes.includes('editor')">

      <h1>{{ $t('controlTitle') }}</h1>

      <div class="module">
        <p>{{ $t('accessDenied') }}</p>
      </div>
    </div>

  </div>

  <div v-else class="centered-block">
    <h2>{{ $t('loginRequiredTitle') }}</h2>
    <p>{{ $t('loginRequiredMessage') }}</p>
  </div>
</template>